import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, CameraOff, LoaderCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import { useBackgroundBlur, type BackgroundEffect } from "@/hooks/useBackgroundBlur";

interface LivestreamCameraProps {
  onExit: () => void;
}

type CameraState = "requesting" | "ready" | "denied" | "unavailable";

const EFFECT_STORAGE_KEY = "livestream_background_effect";
const CONTROLS_HIDE_DELAY_MS = 3_000;

const readStoredEffect = (): BackgroundEffect => {
  if (typeof window === "undefined") return "none";
  const stored = window.localStorage.getItem(EFFECT_STORAGE_KEY);
  return stored === "blur" || stored === "blur-strong" ? "blur" : "none";
};

export const LivestreamCamera = ({ onExit }: LivestreamCameraProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const controlsTimerRef = useRef<number | null>(null);
  const [cameraState, setCameraState] = useState<CameraState>("requesting");
  const [effect, setEffect] = useState<BackgroundEffect>(readStoredEffect);
  const [controlsVisible, setControlsVisible] = useState(true);

  const effectStatus = useBackgroundBlur(videoRef, canvasRef, effect, cameraState === "ready");
  const showCanvas = effect !== "none" && effectStatus === "active";

  const clearControlsTimer = useCallback(() => {
    if (controlsTimerRef.current === null) return;
    window.clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = null;
  }, []);

  const showControls = useCallback(() => {
    clearControlsTimer();
    setControlsVisible(true);
  }, [clearControlsTimer]);

  const scheduleControlsHide = useCallback(() => {
    clearControlsTimer();
    controlsTimerRef.current = window.setTimeout(() => {
      setControlsVisible(false);
      controlsTimerRef.current = null;
    }, CONTROLS_HIDE_DELAY_MS);
  }, [clearControlsTimer]);

  const handleEffectToggle = () => {
    const next: BackgroundEffect = effect === "none" ? "blur" : "none";
    setEffect(next);
    try {
      window.localStorage.setItem(EFFECT_STORAGE_KEY, next);
    } catch {
      /* storage unavailable */
    }
    trackEvent("livestream_background_effect", { effect: next });
  };

  useEffect(() => {
    scheduleControlsHide();
    return clearControlsTimer;
  }, [clearControlsTimer, scheduleControlsHide]);

  // Publish the real camera height so overlays can anchor flush below it,
  // regardless of viewport units, borders, or aspect-ratio breakpoints.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const root = document.documentElement;
    const update = () => {
      root.style.setProperty("--livestream-camera-h", `${el.getBoundingClientRect().height}px`);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
      root.style.removeProperty("--livestream-camera-h");
    };
  }, []);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let cancelled = false;

    const startCamera = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraState("unavailable");
        trackEvent("livestream_camera_failure", { reason: "unsupported" });
        return;
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: "user",
            width: { ideal: 1280 },
            height: { ideal: 720 },
            aspectRatio: { ideal: 16 / 9 },
          },
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
        setCameraState("ready");
        trackEvent("livestream_camera_ready");
      } catch (error) {
        const permissionDenied = error instanceof DOMException &&
          (error.name === "NotAllowedError" || error.name === "SecurityError");
        setCameraState(permissionDenied ? "denied" : "unavailable");
        trackEvent("livestream_camera_failure", {
          reason: permissionDenied ? "permission_denied" : "unavailable",
        });
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      stream?.getTracks().forEach((track) => track.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`livestream-camera ${controlsVisible ? "" : "livestream-camera-controls-hidden"}`}
      aria-label="Live camera preview"
      onMouseEnter={showControls}
      onMouseLeave={scheduleControlsHide}
      onFocusCapture={showControls}
      onBlurCapture={scheduleControlsHide}
    >
      <video
        ref={videoRef}
        className={`livestream-camera-video ${cameraState === "ready" && !showCanvas ? "is-ready" : ""}`}
        
        autoPlay
        muted
        playsInline
        aria-label="Mirrored live camera preview"
      />

      {effect !== "none" && (
        <canvas
          ref={canvasRef}
          className={`livestream-camera-video livestream-camera-canvas ${showCanvas ? "is-ready" : ""}`}
          aria-hidden="true"
        />
      )}

      {cameraState !== "ready" && (
        <div className="livestream-camera-status" role="status" aria-live="polite">
          {cameraState === "requesting" ? (
            <><LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" /><span>Connecting camera…</span></>
          ) : cameraState === "denied" ? (
            <><CameraOff className="h-5 w-5" aria-hidden="true" /><span>Allow camera access to show your preview</span></>
          ) : (
            <><Camera className="h-5 w-5" aria-hidden="true" /><span>Camera preview unavailable</span></>
          )}
        </div>
      )}

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={`livestream-exit livestream-effects ${effect !== "none" ? "livestream-effects-active" : ""}`}
        onClick={handleEffectToggle}
        aria-label={effect === "none" ? "Blur background" : "Turn off background blur"}
        aria-pressed={effect !== "none"}
        title={effect === "none" ? "Blur background" : "Turn off background blur"}
      >
        {effectStatus === "loading" ? (
          <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        ) : (
          <span className="material-icons livestream-effects-icon" aria-hidden="true">
            {effect === "none" ? "blur_off" : "blur_on"}
          </span>
        )}
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onExit}
        className="livestream-exit"
        aria-label="Exit Livestream mode"
        title="Exit Livestream mode"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </Button>
    </section>
  );
};
